import React, { useEffect, useState } from 'react';
import { Facilitator } from '.';
import {
  Avatar,
  Divider,
  Flex,
  Stack,
  Text,
  Tooltip
} from '@mantine/core';
import FacilitatorItem from './FacilitatorItem';
import ModalWrapper from '../../elements/ModalWrapper';
import { ApiService } from '../../../constants';
import { HttpService } from '../../../utilities';

interface FacilitatorProps {
  facilitators: Facilitator[];
}

const FacilitatorSection: React.FC<FacilitatorProps> = ({
  facilitators
}) => {
  const [opened, setOpened] = useState(false);
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;

    const loadImages = async () => {
      const uniqueUrls: string[] = Array.from(
        new Set<string>(
          (facilitators || [])
            .map((facilitator) =>
              typeof facilitator.urlprofile === 'string'
                ? facilitator.urlprofile.trim()
                : ''
            )
            .filter((url) => url.length > 0)
        )
      );

      const result: Record<string, string> = {};

      await Promise.all(
        uniqueUrls.map(async (url) => {
          try {
            const response = await HttpService.get<string>(
              ApiService.beelajar.getImageMoodle(url)
            );

            const cdnUrl = response?.data;

            if (
              typeof cdnUrl === 'string' &&
              /^https?:\/\//i.test(cdnUrl.trim())
            ) {
              result[url] = cdnUrl.trim();
            }
          } catch (error) {
            console.error(
              'Failed to load facilitator image URL:',
              error
            );
          }
        })
      );

      if (!cancelled) {
        setImageUrls((prev) => ({
          ...prev,
          ...result
        }));
      }
    };

    loadImages();

    return () => {
      cancelled = true;
    };
  }, [facilitators]);

  if (facilitators.length < 1) {
    return (
      <Text fw={700} fz={12} c="gray">
        No Facilitator
      </Text>
    );
  }

  return (
    <>
      <Tooltip.Group openDelay={300} closeDelay={100}>
        <Avatar.Group spacing="sm">
          {facilitators.length > 4
            ? facilitators.slice(0, 4).map((facilitator, i) => {
                if (i < 3) {
                  return (
                    <FacilitatorItem
                      {...facilitator}
                      key={i}
                    />
                  );
                }

                return (
                  <FacilitatorItem
                    {...facilitator}
                    key={i}
                    type="more"
                    total={facilitators.length - 3}
                    onClick={() => setOpened(true)}
                  />
                );
              })
            : facilitators.map((facilitator, i) => (
                <FacilitatorItem
                  {...facilitator}
                  key={i}
                />
              ))}
        </Avatar.Group>
      </Tooltip.Group>

      <ModalWrapper
        isVisible={opened}
        onClose={() => setOpened(false)}
        title="List Facilitator"
      >
        {facilitators.map((facilitator, i) => {
          const sourceUrl =
            typeof facilitator.urlprofile === 'string'
              ? facilitator.urlprofile.trim()
              : '';

          const imageUrl = sourceUrl
            ? imageUrls[sourceUrl] || undefined
            : undefined;

          return (
            <React.Fragment key={i}>
              <Flex gap={16}>
                <Avatar
                  alt={facilitator.facilitatorname || facilitator.email}
                  src={imageUrl}
                  radius="xl"
                />

                <Stack gap={0}>
                  <Text fz={16} fw={700}>
                    {facilitator.facilitatorname}
                  </Text>

                  <Text fz={12} fw={600} c="gray">
                    {facilitator.email}
                  </Text>
                </Stack>
              </Flex>

              {i !== facilitators.length - 1 && (
                <Divider my={8} />
              )}
            </React.Fragment>
          );
        })}
      </ModalWrapper>
    </>
  );
};

export default FacilitatorSection;